import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import { deriveMarketResolutionThresholdPolicy, resolutionThresholdStateAt } from '../../helpers/resolution-threshold.js';
import { hashDiagnosticEntity } from '../../tx-engine/diagnostics/lifecycle.js';
import { sdkEvents } from '../events.js';
const MAX_TIMEOUT_MS = 2_147_483_647;
/** The single client clock for one market detail threshold. */
export function useMarketResolutionThreshold(input) {
  const $ = _c(13);
  const {
    config,
    marketPubkey,
    outcomeCount,
    resolvedAt,
    status,
    tradingStartedAt
  } = input;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Set();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const reportedDiagnostics = useRef(t0);
  let t1;
  if ($[1] !== config || $[2] !== input || $[3] !== outcomeCount) {
    t1 = () => stateFor(input, deriveMarketResolutionThresholdPolicy({
      config,
      outcomeCount
    }));
    $[1] = config;
    $[2] = input;
    $[3] = outcomeCount;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const [state, setState] = useState(t1);
  const publishedState = useRef(state);
  let t2;
  let t3;
  if ($[5] !== config || $[6] !== marketPubkey || $[7] !== outcomeCount || $[8] !== resolvedAt || $[9] !== status || $[10] !== tradingStartedAt) {
    t2 = () => {
      const policy = deriveMarketResolutionThresholdPolicy({
        config,
        outcomeCount
      });
      let timeout;
      let disposed = false;
      const refresh = () => {
        const next = stateFor({
          status,
          tradingStartedAt,
          resolvedAt
        }, policy);
        reportInvalidPolicy(marketPubkey, next, reportedDiagnostics.current);
        if (!sameState(publishedState.current, next)) {
          publishedState.current = next;
          setState(next);
        }
        if (next.kind !== "ready" || next.nextDropAtMs === null) {
          return;
        }
        const delayMs = Math.max(0, next.nextDropAtMs - Date.now());
        timeout = setTimeout(() => {
          if (!disposed) {
            refresh();
          }
        }, Math.min(delayMs, MAX_TIMEOUT_MS));
      };
      refresh();
      return () => {
        disposed = true;
        if (timeout !== undefined) {
          clearTimeout(timeout);
        }
      };
    };
    t3 = [config, marketPubkey, outcomeCount, resolvedAt, status, tradingStartedAt];
    $[5] = config;
    $[6] = marketPubkey;
    $[7] = outcomeCount;
    $[8] = resolvedAt;
    $[9] = status;
    $[10] = tradingStartedAt;
    $[11] = t2;
    $[12] = t3;
  } else {
    t2 = $[11];
    t3 = $[12];
  }
  useEffect(t2, t3);
  return state;
}
function stateFor(input, policy) {
  return resolutionThresholdStateAt({
    policy,
    status: input.status,
    tradingStartedAt: input.tradingStartedAt,
    resolvedAt: input.resolvedAt,
    nowTs: BigInt(Math.floor(Date.now() / 1000))
  });
}
function reportInvalidPolicy(marketPubkey, state, reported) {
  if (state.kind !== 'unavailable' || state.reason === 'config-unavailable') return;
  const diagnosticKey = `${marketPubkey}:${state.reason}`;
  if (reported.has(diagnosticKey)) return;
  reported.add(diagnosticKey);
  sdkEvents.emit('resolution-threshold:diagnostic', {
    entityHash: hashDiagnosticEntity(marketPubkey),
    reason: state.reason
  });
}
function sameState(previous, next) {
  if (previous.kind !== next.kind) return false;
  if (previous.kind === 'unavailable') return next.kind === 'unavailable' && previous.reason === next.reason;
  if (next.kind === 'unavailable') return false;
  return previous.thresholdPpb === next.thresholdPpb && previous.nextDropAtMs === next.nextDropAtMs && previous.frozen === next.frozen;
}